import type { ApiErrorBody, ApiErrorCode } from '@/types/api';

/**
 * A structured failure from the API.
 *
 * <p>Carrying the machine-readable `code` rather than only a message is what lets the UI
 * branch meaningfully: a `RIDE_FULL` should offer similar rides, a `TOKEN_EXPIRED`
 * should refresh silently, and a `VALIDATION_FAILED` should highlight a field. Matching
 * on message strings would break the moment someone improves the wording.
 */
export class ApiError extends Error {
  readonly code: ApiErrorCode;
  readonly status: number;
  readonly traceId: string;
  readonly details: Record<string, unknown>;

  constructor(status: number, body: ApiErrorBody) {
    super(body.message);
    this.name = 'ApiError';
    this.status = status;
    this.code = body.code;
    this.traceId = body.traceId;
    this.details = body.details ?? {};
  }

  /**
   * For a network failure or a response that is not the documented error shape -- a
   * proxy error page, a truncated body, a CloudFront 503. The UI still needs an
   * ApiError, not a bare TypeError from `fetch`.
   */
  static fromUnknown(status: number, traceId = 'unknown'): ApiError {
    return new ApiError(status, {
      code: status >= 500 ? 'INTERNAL_ERROR' : 'UNPROCESSABLE',
      message:
        status === 0
          ? 'Could not reach WeDrive. Check your connection and try again.'
          : 'Something went wrong. Please try again.',
      timestamp: new Date().toISOString(),
      traceId,
    });
  }

  /** True when retrying the identical request could plausibly succeed. */
  get isRetryable(): boolean {
    return this.status === 0 || this.status === 429 || this.status >= 500;
  }

  get isAuthFailure(): boolean {
    return this.code === 'UNAUTHENTICATED' || this.code === 'TOKEN_EXPIRED';
  }
}
